import cn from "classnames"
import { useRef } from "react"

import { DatePicker } from "@/atoms"
import { getDate } from "@/helpers/date.helpers"
import { DAYS_AGO, PAGE_NUMBER, SEEN_TIMESTAMP } from "@/helpers/filters.helper"
import { useFilters } from "@/hooks/useFilters"
import { useLogger } from "@/hooks/useLogger"

import styles from "./DatePicker.module.scss"

import type { DatePickerFilterProps } from "."

export const DatePickerFilter = ({
  placeholder,
  isLoading,
  placement,
  type
}: DatePickerFilterProps) => {
  const { searchParamsProps } = useFilters()
  const { original: searchParams, updateSearchParams: setSearchParams } =
    searchParamsProps
  const nameParam = type === "timestamp" ? SEEN_TIMESTAMP : DAYS_AGO
  const defaultValue = useRef(
    (() => {
      const daysAgo = Number(searchParams.get(nameParam))

      if (!daysAgo) return undefined
      if (type === "timestamp") return getDate(daysAgo * 1000)

      return getDate().subtract(daysAgo, "day")
    })()
  ).current
  const presets = [
    { label: "Last 24 hours", value: getDate().subtract(1, "day") },
    { label: "Last 7 days", value: getDate().subtract(7, "day") },
    { label: "Last 30 days", value: getDate().subtract(30, "day") },
    { label: "Last 90 days", value: getDate().subtract(90, "day") }
  ]
  const { logger, EVENTS } = useLogger()

  return (
    <DatePicker
      className={cn(isLoading && styles.disabled)}
      disabled={isLoading}
      defaultValue={defaultValue}
      placeholder={placeholder}
      presets={presets}
      onChange={(date, dateString) => {
        setSearchParams((prev) => {
          prev.set(PAGE_NUMBER, "0")

          if (!date || !dateString) {
            prev.delete(nameParam)
          } else if (type === "timestamp")
            prev.set(nameParam, `${date.utc().unix()}`)
          else {
            const value = Array.isArray(dateString) ? dateString[0] : dateString
            const daysAgo = getDate().diff(getDate(value), "day")

            if (!daysAgo) prev.delete(nameParam)
            else prev.set(nameParam, `${daysAgo}`)
          }

          return prev
        })

        logger.info(EVENTS.ANALYTIC_EVENTS.TAB.FILTER.QUICK_FILTERS, {
          placement,
          name: "DatePicker"
        })
      }}
      disabledDate={(current) => {
        const disabled =
          type === "timestamp"
            ? current > getDate().endOf("day")
            : current >= getDate().startOf("day")

        return disabled || current < getDate().subtract(10, "year")
      }}
    />
  )
}
